import React, { useEffect, useState } from "react";
import api from "../../lib/api.ts";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import Pagination from "../common/Pagination";
// currency utilities
import { useCurrencyUtils } from "../../utils/currencyUtils";

const ListingFee = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [listingFees, setListingFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(10);

  const { user } = useAuth();

  const fetchVehicleListingFee = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/vehiclelistingfee/paginated`, {
        params: {
          page: page,
          limit: limit,
          orderBy: "id",
          order: "desc",
          filters: {
            sellerId: user.seller.id || user.sellerId,
          },
        },
      });

      console.log("Listing Fee Response:", response.data);

      if (response.data && response.data.data) {
        setListingFees(response.data.data);
        setTotalPages(response.data.totalPages || 1);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (err) {
      console.error("Error fetching listing fees:", err);
      setError(translate("listingFees.messages.failedToFetch"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchVehicleListingFee(currentPage);
    }
  }, [user, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-AE" : "en-US",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      },
    );
  };

  if (loading) {
    return (
      <div className=" shadow-md" dir={isRTL ? "rtl" : "ltr"} lang={language}>
        {/* Header */}
        <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]">
            {/* Left Side Grouping */}
            <div className="flex flex-col justify-center">
              <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
                {translate("listingFees.heading")}
              </h2>
            </div>

            {/* Right Side Grouping (Kept for height and alignment consistency) */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 ms-auto lg:ms-0 w-full lg:w-auto"></div>
          </div>
        </div>

        <div className="px-3 py-6">
          <div className="animate-pulse">
            <div className="h-6 bg-page rounded w-1/3 mb-6"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-card rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className=" shadow-md" dir={isRTL ? "rtl" : "ltr"} lang={language}>
        {/* Header */}
        <div className="font-medium rounded-t-[16px]">
          <div className="p-[20px] text-lg rounded-t-[12px] font-semibold text-white bg-primary-soft border-b border-black pb-2">
            <h2 className="text-base sm:text-lg font-semibold">
              {translate("listingFees.heading")}
            </h2>
          </div>
        </div>
        <div className="px-3 py-6 text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => fetchVehicleListingFee(currentPage)}
            className="mt-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary-hover transition-colors"
          >
            {translate("listingFees.buttons.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-card rounded-lg shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      <div className=" font-medium rounded-t-[16px]">
        <div className="p-[20px] text-lg rounded-t-[12px] font-semibold text-white bg-primary-soft">
          <h2 className="text-white sm:text-lg font-semibold">
            {translate("listingFees.heading")}
          </h2>
        </div>
      </div>

      <div className="px-3 py-6">
        {/* Table */}
        <div className="overflow-x-auto overflow-y-hidden rounded-lg shadow-sm">
          <table className="min-w-full table-auto">
            <thead className="bg-page sticky top-0 z-10">
              <tr>
                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.vehicle")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.auction")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.lane")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.runNo")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.listingFee")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.vat")}
                   {/* (
                  {listingFees[0]?.vatPercentage}%) */}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.total")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.status")}
                </th>

                <th
                  className={`px-4 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap`}
                >
                  {translate("listingFees.tableHeaders.date")}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {listingFees.map((fee, index) => (
                <tr key={fee.id || index} className="hover:bg-page">
                  <td className="px-6 py-4 ">
                    <div>
                      <div className="text-sm font-medium w-[200px] text-white">
                        {fee.vehicle?.name || "-"}
                      </div>
                      <div className="text-sm text-white/70">
                        {fee.vehicle?.lotNumber || "-"}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-white">
                        {fee.auctionLane?.auction?.name || "-"}
                      </div>
                      <div className="text-sm text-white/70">
                        {fee.auctionLane?.auction?.startDate
                          ? formatDate(fee.auctionLane.auction.startDate)
                          : "-"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {fee.auctionLane?.lane?.name || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                    {fee.runNo}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                    {displayPriceWithSymbol(convertPrice(fee.listingFee))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                    {displayPriceWithSymbol(convertPrice(fee.vatAmount))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-primary">
                    {displayPriceWithSymbol(convertPrice(fee.totalListingFee))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {fee.status === "completed" ? (
                      <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                        {translate("listingFees.status.charged")}
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700">
                        {translate("listingFees.status.pending")}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/50">
                    {formatDate(fee.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {listingFees.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-white">
                {translate("listingFees.messages.noListingFeesFound")}
              </h3>
              <p className="mt-1 text-sm text-description">
                {translate("listingFees.messages.noListingFeesAvailable")}
              </p>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingFee;
